'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getProfile } from '@/app/actions/profile';
import { isRenderRoom } from '@/components/quiz/kit/carousel/render-protocol';

const POSTHOG_KEY = 'phc_azI2iL6e37dP8gMuW0vB37r4uK2giduafttsZx4TJwt';
const POSTHOG_HOST = 'https://g.meetmy.io';

// Exception values that are not real bugs and should never reach error tracking.
// - NEXT_REDIRECT / NEXT_NOT_FOUND: Next.js implements redirect()/notFound() by throwing
//   these sentinel errors, which it catches internally. Autocapture sees them as unhandled.
// - standardSelectors: frameless injected-script error from a browser extension, not our code.
const IGNORED_EXCEPTION_PATTERNS = ['NEXT_REDIRECT', 'NEXT_NOT_FOUND', 'standardSelectors'];

if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_ENV === 'production') {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
    opt_in_site_apps: true,
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    // `PostHogPageView` below is the only place a pageview is captured. The
    // `defaults` above turn on `capture_pageview: 'history_change'`, which sent
    // a second one for the same navigation and counted every view twice.
    capture_pageview: false,
    before_send: (event) => {
      if (event?.event === '$exception') {
        const exceptionList = event.properties?.$exception_list ?? [];
        const values = exceptionList.map((e: { value?: string }) => e?.value ?? '').join(' ');
        if (IGNORED_EXCEPTION_PATTERNS.some((pattern) => values.includes(pattern))) {
          return null;
        }
      }
      return event;
    },
  });
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return <PHProvider client={posthog}>{children}</PHProvider>;
}

/**
 * Is PostHog still carrying somebody's identity in this browser?
 *
 * Not `distinct_id !== $device_id`: after a `reset()` the distinct id is a
 * fresh uuid that never equals `$device_id` again, so that comparison reads as
 * "identified" forever and resets on every load.
 *
 * `$user_state` is internal persistence, not public API — a rename would
 * silently return `false` here rather than throw, which is why `posthog-js` is
 * pinned exact.
 */
function hasStoredIdentity(): boolean {
  return posthog.get_property('$user_state') === 'identified';
}

export function PostHogIdentify({ isAuthenticated }: { isAuthenticated: boolean }) {
  const identifiedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // Session ended: drop the once-per-session identify guard so a different
      // user logging in in the same tab re-fetches their profile.
      identifiedRef.current = false;
      // And the identity with it. `signOut()` resets, but an expired cookie or
      // a shared browser ends a session without it, leaving the last person
      // live in localStorage — and posthog-js will not switch an id that is
      // already identified, so the next visitor signs up and buys as them.
      // Resetting on a cookieless load rather than at sign-in is what puts the
      // events *before* the sign-in on the right person too.
      if (hasStoredIdentity()) posthog.reset();
      return;
    }
    if (identifiedRef.current) return;

    getProfile()
      .then((result) => {
        if (result.success) {
          const { user, roles } = result.data;
          // The same reconciliation from the other side. Without it the
          // identify below is a silent no-op that keeps the stale person.
          if (hasStoredIdentity() && posthog.get_distinct_id() !== user.id) posthog.reset();
          posthog.setPersonPropertiesForFlags({ username: user.username });
          posthog.identify(user.id, {
            email: user.email,
            name: user.name,
            username: user.username,
            is_tutor: roles.tutor,
            is_student: roles.student,
          });
          identifiedRef.current = true;
        }
      })
      .catch(() => {
        // Non-critical: silently ignore network failures during PostHog identification
      });
  }, [isAuthenticated]);

  return null;
}

export function PostHogPageView(): null {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (pathname && !isRenderRoom(pathname)) {
      let url = window.origin + pathname;
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`;
      }
      posthog.capture('$pageview', {
        $current_url: url,
      });
    }
  }, [pathname, searchParams]);

  return null;
}
